import React, { useState } from "react";
import PortalPage from "../PortalPage";
import { ReactComponent as ReportingIcon } from 'assets/icons/reports-filled.svg';
import { ReactComponent as BuildCircle } from "assets/icons/build-circle.svg";
import { ReactComponent as AddChart } from "assets/icons/add-chart.svg";
import { IConfirmProps } from "../../ui/ConfirmationBox";
import { LabelledCheckbox } from "../../ui/LabelledCheckbox";
import {
    PersonOutlined, AssignmentTurnedInOutlined, DateRangeOutlined, CheckOutlined
} from '@mui/icons-material';
import * as api from 'shared/services/reportingApi';
import { Paper, Tabs, Tab, Chip } from "@mui/material";
import { TabPanel } from "../../ui/TabPanel";
import { List } from "../../ui/Lists";
import { UserChips, UserListItem } from "./GNHExemptionReportingView";
import DateRangePicker from "./DateRangePicker";
import { blobDownload } from "./blobDownload";
import { TakeFiveReportQuery, TakeFiveRiskEnum } from "../../../shared/models/api/models";

export const TakeFiveReport = () => {
    const MaxResultsPerReport = 60000;
    const [exportState, setExportState] = useState("ready" as "ready" | "busy");
    const [confirmProps, setConfirmProps] = useState(null as IConfirmProps | null);
    const [stateVersion, setStateVersion] = useState(0);

    const [allUsers, setAllUsers] = useState([] as api.User[]);
    const [selectedUsers, setSelectedUsers] = useState([] as api.User[]);
    const [selectAllUsers, setSelectAllUsers] = useState(true);
    const [usersByNameFilter, setUsersByNameFilter] = useState("");
    const [usersByNameExpanded, setUsersByNameExpanded] = useState(false);
    const [usersByNameSummary, setUsersByNameSummary] = useState("");

    const [selectedSupervisors, setSelectedSupervisors] = useState([] as api.User[]);
    const [supervisorsFilter, setSupervisorsFilter] = useState("");
    const [supervisorsExpanded, setSupervisorsExpanded] = useState(false);
    const [supervisorsSummary, setSupervisorsSummary] = useState("");
    const [supervisors, setSupervisors] = useState([] as api.User[]);

    const [lowRisk, setLowRisk] = useState(true);
    const [highRisk, setHighRisk] = useState(true);

    const [userFilterTab, setUserFilterTab] = useState(0);
    const [includeHazards, setIncludeHazards] = useState(false);

    const getExportFilter = () => {
        let userIds = selectedUsers.map(user => user.id);
        let supervisorIds = selectedSupervisors.map(user => user.id);

        let riskLevels = [] as TakeFiveRiskEnum[];
        if (lowRisk) riskLevels.push(TakeFiveRiskEnum.Low);
        if (highRisk) riskLevels.push(TakeFiveRiskEnum.High);

        return {
            userIds: userIds,
            supervisorIds: supervisorIds,
            riskLevels: riskLevels,
            requestDateFrom: requestDateFrom,
            requestDateTo: requestDateTo,
            includeHazards: includeHazards
        } as TakeFiveReportQuery;
    };

    const downloadExportResults = async () => {
        if (exportState === "ready") {
            const filter = getExportFilter();

            if (filter) {
                setExportState("busy");

                api.downloadTakeFiveResults(filter)
                    .then(file => {
                        setExportState("ready");
                        blobDownload(file);
                    })
                    .catch(e => {
                        const message = (e instanceof RangeError) ? e.message : "Error fetching data. ";
                        confirm(message, ["Close"], "Close");
                        setExportState("ready");
                    });
            }
        }
    };

    const confirm = async (message: string, opts?: string[], defaultOption?: string, cancelOption?: string): Promise<string> => {
        return new Promise<string>((resolve) => {
            setConfirmProps({
                message: message,
                options: opts ?? ["OK", "Cancel"],
                defaultOption: defaultOption,
                setResult: (value) => {
                    setConfirmProps({} as IConfirmProps);
                    resolve(value);
                }
            });
        });
    };

    const setSelectAllUsersAndClearSelections = (value: boolean) => {
        if (value) {
            confirm("Are you sure you want to include all users? Including all users will reset all user filters.", ["All Users", "Cancel"], "All Users").then(result => {
                if (result === "All Users") {
                    setSelectedUsers([]);
                    setSelectedSupervisors([]);

                    setSelectAllUsers(true);
                } else {
                    setSelectAllUsers(false);
                }
            });
        } else {
            setSelectAllUsers(value);
        }
    };

    const searchSupervisorsByName = async (name: string) => {
        const result = await api.getSupervisorsByName(name);

        if (result) {
            setSupervisors(result);
            setSupervisorsSummary(result.length + " search results for the search \"" + name + "\"");
        }

        return result;
    };

    const searchUsersByName = async (name: string) => {
        const result = await api.getUsersByName(name);

        if (result) {
            setAllUsers(result);
            setUsersByNameSummary(result.length + " search results for the search \"" + name + "\"");
        }

        return result;
    };

    const forceStateChange = () => {
        setStateVersion(stateVersion + 1);
    };

    const toggleItem = <T,>(
        item: T,
        arr: T[],
        comparerPredicate: (value: T) => boolean,
        setter: (value: T[]) => void
    ): boolean => {
        const idx = arr.findIndex(comparerPredicate);

        var result = false;

        if (idx === -1) {
            result = true;
            arr.push(item);
        } else {
            arr.splice(idx, 1);
        }

        setter(arr);
        forceStateChange();

        return result;
    };

    const userFilterAdded = () => {
        if (selectAllUsers) {
            setSelectAllUsers(false);
        }
    }

    const toggleUser = (usr: api.User) => {
        if (toggleItem(usr, selectedUsers, u => { return u.id === usr.id }, setSelectedUsers) === true) {
            userFilterAdded();
        }
    };

    const toggleSupervisor = (usr: api.User) => {
        if (toggleItem(usr, selectedSupervisors, u => { return u.id === usr.id }, setSelectedSupervisors) === true) {
            userFilterAdded();
        }
    };

    const selectItems = <T,>(
        srcItems: T[],
        destinationItems: T[],
        comparer: (a: T, b: T) => boolean,
        setter: (items: T[]) => void
    ) => {
        for (var i = 0; i < srcItems.length; i++) {
            // if its not already selected, add it to the list
            if (destinationItems.findIndex(d => comparer(d, srcItems[i])) === -1) {
                destinationItems.push(srcItems[i]);
            }
        }

        setter(destinationItems);
        forceStateChange();
    };

    const today = new Date()
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const [requestDateFrom, setRequestDateFrom] = useState(yesterday as Date | null);
    const [requestDateTo, setRequestDateTo] = useState(today as Date | null);

    const setRequestDateRange = (dateFrom: Date | null, dateTo: Date | null): void => {
        setRequestDateFrom(dateFrom);
        setRequestDateTo(dateTo);
    };

    return (
        <PortalPage module=""
            page="reporting"
            title={"Take 5 reporting"}
            route={"/reporting/take-five"}
            loadingState={exportState}
            icon={ReportingIcon}
            confirmationProps={confirmProps}>

            <div className="main-body-container reporting-module">

                <div className="section-1">
                    <div className="header">
                        <span className="section-1 icon"><BuildCircle /></span>
                        <span className="section-1 text">Build a report</span>
                    </div>
                </div>

                <div className="section-2">
                    <div className="header">
                        <span className="icon"><AddChart /></span>
                        <span className="text">Add data</span>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><PersonOutlined /></span>
                        <span className="text">User</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <LabelledCheckbox
                            checkedValue={selectAllUsers}
                            toggle={() => { setSelectAllUsersAndClearSelections(!selectAllUsers); }}>Select all users</LabelledCheckbox>
                    </div>
                    <div className="body">or</div>
                    <div className="body">
                        <Paper>
                            <Tabs value={userFilterTab} onChange={(e, v) => { setUserFilterTab(v); }}>
                                <Tab label="Find a user" />
                                <Tab label="Users by supervisor" />
                            </Tabs>

                            <TabPanel value={userFilterTab} index={0}>
                                <List<api.User>
                                    summary={usersByNameSummary}
                                    dataSource={allUsers}
                                    listItemType={UserListItem}
                                    filterText={usersByNameFilter}
                                    setFilterText={setUsersByNameFilter}
                                    expanded={usersByNameExpanded}
                                    setExpanded={setUsersByNameExpanded}
                                    buildKey={item => item.id}
                                    filterAsync={searchUsersByName}
                                    toggleSelected={toggleUser}
                                    selectItems={
                                        (filteredItems) => selectItems(
                                            filteredItems,
                                            selectedUsers,
                                            (a, b) => a.id === b.id,
                                            setSelectedUsers
                                        )
                                    }
                                    noResultsMessage="No users found"
                                    filterTextPlaceholder="Enter first name, last name or email address"
                                    selected={selectedUsers} />
                            </TabPanel>

                            <TabPanel value={userFilterTab} index={1}>
                                <List<api.User>
                                    summary={supervisorsSummary}
                                    dataSource={supervisors}
                                    listItemType={UserListItem}
                                    filterText={supervisorsFilter}
                                    setFilterText={setSupervisorsFilter}
                                    expanded={supervisorsExpanded}
                                    setExpanded={setSupervisorsExpanded}
                                    buildKey={item => item.id}
                                    filterAsync={searchSupervisorsByName}
                                    toggleSelected={toggleSupervisor}
                                    selectItems={
                                        (filteredItems) => selectItems(
                                            filteredItems,
                                            selectedSupervisors,
                                            (a, b) => a.id === b.id,
                                            setSelectedSupervisors
                                        )
                                    }
                                    noResultsMessage="No supervisors found"
                                    filterTextPlaceholder="Enter first name, last name or email address"
                                    selected={selectedSupervisors} />
                            </TabPanel>

                        </Paper>

                        <span>
                            <UserChips prefix="User" values={selectedUsers} removeItem={toggleUser} />
                            <UserChips prefix="Supervisor" values={selectedSupervisors} removeItem={toggleSupervisor} />
                            &nbsp;
                            {selectAllUsers &&
                                <Chip onDelete={() => { setSelectAllUsersAndClearSelections(false); }} label={<><b>User: </b>All</>}></Chip>
                            }
                            &nbsp;
                        </span>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><AssignmentTurnedInOutlined /></span>
                        <span className="text">Risk Level</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <LabelledCheckbox checkedValue={lowRisk} toggle={() => setLowRisk(!lowRisk)}>Low Risk</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={highRisk} toggle={() => setHighRisk(!highRisk)}>high Risk</LabelledCheckbox>                        
                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><DateRangeOutlined /></span>
                        <span className="text">Take 5 Date</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <DateRangePicker
                            fromDate={requestDateFrom ?? yesterday}
                            toDate={requestDateTo ?? today}
                            setter={(f, t) => {
                                setRequestDateRange(f ? f : null, t ? t : null);
                            }}
                        />
                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><CheckOutlined /></span>
                        <span className="text">Hazards</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <LabelledCheckbox checkedValue={includeHazards} toggle={() => setIncludeHazards(!includeHazards)}>Include Take 5 hazards</LabelledCheckbox>

                    </div>
                </div>

                <div className="section-3">
                    <div className="body" style={{ textAlign: "center" }}>
                        <button className={"button-face export-state-" + exportState} onClick={downloadExportResults}>Export to CSV</button>
                    </div>
                </div>

                {((exportState === 'busy')) &&
                    <div className="export-busy-container">
                        <div className="export-busy-message">We're fetching your data...</div>
                    </div>
                }

            </div>
        </PortalPage>
    );
}